import { React, useState } from "react";
import {
	Menu,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { IconButton } from "@prestosports/commons-ui";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import Close32 from "@carbon/icons-react/lib/close/32";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { EnumUtils, ObjectUtils } from "@prestosports/prestostats-core/Common";
import Chip from "@material-ui/core/Chip";
import {
	getGameType,
	MatchTiebreakType
} from "@prestosports/prestostats-core/Tennis";
import classnames from "classnames";
import OverflowMenuVertical32 from "@carbon/icons-react/lib/overflow-menu--vertical/32";
import matchStyles from "../styles/Match.styles";
import { useTeamsContext } from "../../../state/teams/context";
import { useMatchesContext } from "../../../state/matches/context";
import MatchEdit from "./MatchEdit";
import commonStyles from "../../common/styles/Common.styles";
import DeleteConfirmationDialog from "../../common/components/DeleteConfirmationDialog";
import { useEventContext } from "../../../state/events/context";
import MatchDialogTitle from "./MatchDialogTitle";
import { useAppContext } from "../../../state/app/context";
import TeamRow from "./TeamRow";
import NumberFormat from "../../../util/number-format";
import MenuActions from "../../common/components/MenuActions";
import { isDefined } from "../../../util/object-util";

const Match = ({ match = {}, disableActions = false, index }) => {
	const commonClasses = commonStyles();
	const classes = matchStyles({ disableActions });

	const { state: appState } = useAppContext();
	const isSubmitting = appState.isSubmitting();

	const { deleteMatch, editMatch } = useMatchesContext();
	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam() || {};
	const awayTeam = teamState.awayTeam() || {};

	const { state: eventState } = useEventContext();
	const { gameConfiguration } = eventState.currentEventSettings() || {};

	const [editMatchDialog, setEditMatchDialog] = useState(false);
	const [showActions, setShowActions] = useState(null);
	const [matchToDelete, setMatchToDelete] = useState(null);
	const [formData, setFormData] = useState({});

	const { matchNumber, isSingle, winnerId, away, home, sets, tiebreakSets } =
		match;

	const matchIncomplete = away?.incompleteType || home?.incompleteType;

	const isNoPlayerMatch = home?.noPlayer && away?.noPlayer;

	const matchHeader = `${isSingle ? "SINGLE" : "DOUBLE"} ${
		disableActions ? "#" : ""
	}${matchNumber} ${
		(winnerId || isNoPlayerMatch || matchIncomplete) && !disableActions
			? "  |  FINAL"
			: ""
	}`;

	const tiebreakType = EnumUtils.getByCode(
		MatchTiebreakType.default,
		match.matchTiebreakType
	);

	const showColumn = (set) => {
		return set && isDefined(set.home.score) && isDefined(set.away.score);
	};

	const confirmDelete = () => {
		deleteMatch(matchToDelete);
		setMatchToDelete(null);
	};

	const cancelDelete = () => {
		setMatchToDelete(null);
	};

	const handleActionsClose = () => {
		setShowActions(null);
	};

	const matchActions = [
		{
			description: "Edit Match",
			action: () => setEditMatchDialog(true)
		},
		{
			description: "Delete Match",
			action: (m) => setMatchToDelete(m)
		}
	];

	const getRowHeaders = () => {
		const setsToPlay = getGameType(match, gameConfiguration)?.value || 0;

		const headers = [
			{
				align: "left",
				isHeader: true
			}
		];

		const setColumns = [...Array(setsToPlay).keys()].map((i) => {
			return {
				display: showColumn(sets[i]),
				isSet: true,
				label: i + 1,
				index: i
			};
		});

		let tiebreakColumns = [];
		if (
			tiebreakType?.code === MatchTiebreakType.default.Sets.code &&
			tiebreakSets?.length
		) {
			const displayedSets = setColumns.filter((c) => c.display).length;
			tiebreakColumns = [...Array(tiebreakSets?.length).keys()].map((i) => {
				return {
					display: showColumn(tiebreakSets[i]),
					label: displayedSets + i + 1,
					index: i,
					isTiebreakSet: true
				};
			});
		} else if (tiebreakType?.code === MatchTiebreakType.default.Points.code) {
			tiebreakColumns = [
				{
					display: true,
					label: "T",
					index: setsToPlay,
					isTiebreakPoints: true
				}
			];
		}

		const actions = [
			{
				isAction: true
			}
		];

		const columns = [...headers, ...setColumns, ...tiebreakColumns];

		return disableActions ? columns : [...columns, ...actions];
	};

	const columns = getRowHeaders();

	const handleDialogExit = () => {
		setEditMatchDialog(false);
	};

	const handleMatchEdit = async () => {
		await editMatch(formData.values);
		handleDialogExit();
	};

	return (
		<Box className={classes.box}>
			{away && home && (
				<>
					<Box className={classes.tableWrapper}>
						<Table
							size="small"
							className={classnames(classes.table, {
								"fixed-first-child": true,
								"fixed-last-child": !disableActions
							})}
						>
							<colgroup>
								{columns
									.filter((c) => c.isHeader || c.isAction || c.display)
									.map((c, i) => {
										let width = i > 0 ? "58px" : "auto";
										if (c.isAction) {
											width = "30px";
										}
										return <col width={width} key={c.index} />;
									})}
							</colgroup>
							<TableHead>
								<TableRow>
									{columns.map((column) => (
										<>
											{column.isHeader && (
												<TableCell
													component="th"
													align={column.align || "center"}
													key={column.index}
												>
													{index && <Chip label={NumberFormat.withOrdinal(index)} />}
													<Chip variant="outlined" label={matchHeader} />
												</TableCell>
											)}
											{column.display && (
												<TableCell
													component="th"
													align={column.align || "center"}
													key={column.index}
												>
													{(column.isSet ||
														column.isTiebreakSet ||
														column.isTiebreakPoints) && (
														<Chip
															variant={
																column.isTiebreakSet || column.isTiebreakPoints
																	? "default"
																	: "outlined"
															}
															label={column.label}
														/>
													)}
												</TableCell>
											)}
											{column.isAction && (
												<TableCell
													component="th"
													align={column.align || "center"}
													key={column.index}
												>
													<IconButton
														size="medium"
														aria-label="Match Settings"
														aria-haspopup="true"
														onClick={(e) => setShowActions(e.currentTarget)}
													>
														<OverflowMenuVertical32 />
													</IconButton>
												</TableCell>
											)}
										</>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								<TeamRow
									columns={columns}
									teamSide="away"
									match={match}
									team={awayTeam}
								/>
								<TeamRow
									columns={columns}
									teamSide="home"
									match={match}
									team={homeTeam}
								/>
							</TableBody>
						</Table>
					</Box>
					{tiebreakType && (
						<Box display="flex" justifyContent="right" p={1}>
							<Typography className={classes.settingsLabel}>
								Match Tiebreaker: {tiebreakType.name}
							</Typography>
						</Box>
					)}
					<Menu
						id="simple-menu"
						anchorEl={showActions}
						keepMounted
						classes={{ list: classes.actionMenu }}
						open={Boolean(showActions)}
						onClick={handleActionsClose}
						getContentAnchorEl={null}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "right"
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "right"
						}}
					>
						<MenuActions menuItem={match} actions={matchActions} />
					</Menu>
				</>
			)}
			<Dialog
				open={editMatchDialog}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				classes={{ paperWidthSm: classes.paperWidthSm }}
			>
				<DialogTitle id="alert-dialog-title">
					<Box className={commonClasses.textContainer}>
						<MatchDialogTitle match={match} isEdit />
					</Box>
					<IconButton
						onClick={handleDialogExit}
						aria-label="close-dialog"
						className={commonClasses.closeButton}
					>
						<Close32 />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<MatchEdit
						onValidate={(values) => setFormData(values)}
						match={ObjectUtils.deepCopy(match)}
						showRank={false}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleMatchEdit}
						disabled={isSubmitting || !formData.isValid || !editMatchDialog}
						color="primary"
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<DeleteConfirmationDialog
				title="Delete match?"
				message="Are you sure you want to delete this match?"
				open={matchToDelete}
				onCancel={cancelDelete}
				onConfirm={confirmDelete}
				confirmDisabled={isSubmitting}
			/>
		</Box>
	);
};

export default Match;
